import React, { useState } from 'react';
import './App.css';

const DAYS = ['Luni', 'Marti', 'Miercuri', 'Joi', 'Vineri'];

function App() {
  const [classDuration, setClassDuration] = useState(50);
  const [breakDuration, setBreakDuration] = useState(10);
  const [startHour, setStartHour] = useState(8);
  const [startMinute, setStartMinute] = useState(0);
  const [longBreakEnabled, setLongBreakEnabled] = useState(false);
  const [longBreakAfter, setLongBreakAfter] = useState('');
  const [longBreakDuration, setLongBreakDuration] = useState(20);
  const [numClasses, setNumClasses] = useState(7);
  const [timetableName, setTimetableName] = useState('');
  const [timetableData, setTimetableData] = useState({});
  const [legend, setLegend] = useState([
    { abbr: 'Bio', meaning: 'Biologie' },
    { abbr: 'Ch', meaning: 'Chimie' },
    { abbr: 'En', meaning: 'Engleză' },
    { abbr: 'EP', meaning: 'Educație Plastică' },
    { abbr: 'ES', meaning: 'Educație Socială' },
    { abbr: 'ETAP', meaning: 'Educație Tehnologică' },
    { abbr: 'Fiz', meaning: 'Fizică' },
    { abbr: 'Fr', meaning: 'Franceză' },
    { abbr: 'Geo', meaning: 'Geografie' },
    { abbr: 'Ist', meaning: 'Istorie' },
    { abbr: 'IT', meaning: 'Informatică' },
    { abbr: 'LR', meaning: 'Limba Română' },
    { abbr: 'M', meaning: 'Matematică' },
    { abbr: 'Muzică', meaning: 'Educație Muzicală' },
    { abbr: 'R', meaning: 'Religie' },
    { abbr: 'Sport', meaning: 'Educație Fizică și Sport' }
  ]);
  const [editingLegend, setEditingLegend] = useState(null);
  const [manualInputs, setManualInputs] = useState({});
  const [transposedView, setTransposedView] = useState(true);

  const arabicToRoman = (num) => {
    const romanNumerals = {
      1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V',
      6: 'VI', 7: 'VII', 8: 'VIII', 9: 'IX', 10: 'X',
      11: 'XI', 12: 'XII'
    };
    return romanNumerals[num] || num.toString();
  };

  const formatClassName = (input) => {
    if (!input.trim()) return '';
    
    const cleanInput = input.trim();
    
    // Handle edge case like "5C", "6A" - number followed by letter with no space
    const noSpaceMatch = cleanInput.match(/^(\d+|[ivx]+)([a-z])$/i);
    if (noSpaceMatch) {
      let classNumber = noSpaceMatch[1];
      let classLetter = noSpaceMatch[2].toUpperCase();
      
      // Convert arabic numbers to roman
      const numericClass = parseInt(classNumber);
      if (!isNaN(numericClass) && numericClass >= 1 && numericClass <= 12) {
        classNumber = arabicToRoman(numericClass);
      } else if (classNumber.match(/^[ivx]+$/i)) {
        classNumber = classNumber.toUpperCase();
      }
      
      return `Clasa a ${classNumber}-a ${classLetter}`;
    }
    
    // Handle normal cases with separators
    const cleanInputLower = cleanInput.toLowerCase();
    const parts = cleanInputLower.split(/[\s\-_]+/);
    
    if (parts.length >= 2) {
      let classNumber = parts[0];
      let classLetter = parts[1].toUpperCase();
      
      // Convert arabic numbers to roman
      const numericClass = parseInt(classNumber);
      if (!isNaN(numericClass) && numericClass >= 1 && numericClass <= 12) {
        classNumber = arabicToRoman(numericClass);
      } else if (classNumber.match(/^[ivx]+$/i)) {
        // Already roman, just uppercase
        classNumber = classNumber.toUpperCase();
      }
      
      // Ensure single letter
      classLetter = classLetter.charAt(0).toUpperCase();
      
      return `Clasa a ${classNumber}-a ${classLetter}`;
    } else if (parts.length === 1) {
      // Only number provided
      const numericClass = parseInt(parts[0]);
      if (!isNaN(numericClass) && numericClass >= 1 && numericClass <= 12) {
        return `Clasa a ${arabicToRoman(numericClass)}-a`;
      } else if (parts[0].match(/^[ivx]+$/i)) {
        return `Clasa a ${parts[0].toUpperCase()}-a`;
      }
    }
    
    // If format doesn't match, return as-is but capitalized
    return input.trim().split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const calculateTimeSlots = () => {
    const slots = [];
    let currentHour = startHour;
    let currentMinute = startMinute;

    for (let i = 0; i < numClasses; i++) {
      const startTime = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
      
      currentMinute += classDuration;
      if (currentMinute >= 60) {
        currentHour += Math.floor(currentMinute / 60);
        currentMinute = currentMinute % 60;
      }
      
      const endTime = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
      
      slots.push({
        index: i,
        startTime,
        endTime,
        timeRange: `${startTime}-${endTime}`
      });

      if (i < numClasses - 1) {
        // Check if this is where the long break should happen
        const isLongBreakTime = longBreakEnabled && longBreakAfter === endTime;
        const breakTime = isLongBreakTime ? longBreakDuration : breakDuration;
        
        currentMinute += breakTime;
        if (currentMinute >= 60) {
          currentHour += Math.floor(currentMinute / 60);
          currentMinute = currentMinute % 60;
        }
      }
    }

    return slots;
  };
  
  const getAvailableBreakTimes = () => {
    // Calculate slots without long break to get the natural end times
    const tempSlots = [];
    let tempHour = startHour;
    let tempMinute = startMinute;

    for (let i = 0; i < Math.min(numClasses - 1, 6); i++) { // Max 6 options
      tempMinute += classDuration;
      if (tempMinute >= 60) {
        tempHour += Math.floor(tempMinute / 60);
        tempMinute = tempMinute % 60;
      }
      
      const endTime = `${tempHour.toString().padStart(2, '0')}:${tempMinute.toString().padStart(2, '0')}`;
      tempSlots.push({
        index: i + 1,
        endTime,
        label: `După ora ${i + 1} (${endTime})`
      });
      
      // Add regular break for next calculation
      tempMinute += breakDuration;
      if (tempMinute >= 60) {
        tempHour += Math.floor(tempMinute / 60);
        tempMinute = tempMinute % 60;
      }
    }

    return tempSlots;
  };

  const timeSlots = calculateTimeSlots();

  const handleSubjectChange = (day, classIndex, value) => {
    const key = `${day}-${classIndex}`;
    
    if (value === 'MANUAL') {
      setManualInputs(prev => ({ ...prev, [key]: true }));
      return;
    }
    
    if (value === 'GOL') {
      setTimetableData(prev => {
        const newData = { ...prev };
        delete newData[key];
        return newData;
      });
      setManualInputs(prev => {
        const newInputs = { ...prev };
        delete newInputs[key];
        return newInputs;
      });
      return;
    }
    
    setTimetableData(prev => ({
      ...prev,
      [key]: value
    }));
    
    if (value) {
      setManualInputs(prev => {
        const newInputs = { ...prev };
        delete newInputs[key];
        return newInputs;
      });
    }
  };
  
  const handleManualInputChange = (day, classIndex, value) => {
    setTimetableData(prev => ({
      ...prev,
      [`${day}-${classIndex}`]: value
    }));
  };
  
  const handleManualInputBlur = (day, classIndex) => {
    const key = `${day}-${classIndex}`;
    const currentValue = timetableData[key];

    // Keep manual mode if value is not in legend (custom text)
    const isInLegend = legend.some(item => item.abbr === currentValue);
    if (currentValue && !isInLegend) {
      // Keep manual input active for custom values
      return;
    }

    setManualInputs(prev => {
      const newInputs = { ...prev };
      delete newInputs[key];
      return newInputs;
    });
  };

  const getSubjectValue = (day, classIndex) => {
    return timetableData[`${day}-${classIndex}`] || '';
  };

  const getDisplayText = (day, classIndex) => {
    const value = getSubjectValue(day, classIndex);
    if (!value) return '';

    // If it's an abbreviation from legend, return the full meaning
    const legendItem = legend.find(item => item.abbr === value);
    if (legendItem) {
      return legendItem.meaning;
    }

    // Otherwise return the value as is (manual input)
    return value;
  };

  const handlePrint = () => {
    // Save original title
    const originalTitle = document.title;

    // Set new title for PDF export
    const currentYear = new Date().getFullYear();
    let pdfTitle = `Orar (${currentYear}).pdf`;

    if (timetableName.trim()) {
      const formattedClassName = formatClassName(timetableName);
      pdfTitle = `Orar ${formattedClassName} (${currentYear}).pdf`;
    }

    document.title = pdfTitle;

    // Print
    window.print();

    // Restore original title after a short delay
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const getExportFileName = () => {
    const now = new Date();
    const timestamp = now.toISOString()
      .replace('T', '-')
      .replace(/:/g, '-')
      .split('.')[0]; // Remove milliseconds

    if (!timetableName.trim()) {
      return `orar-${timestamp}.json`;
    }

    const cleanInput = timetableName.trim().toLowerCase();

    // Try to extract class number and letter
    let classNumber = '';
    let classLetter = '';

    // Handle formats like "6B", "6 B", "VI B", "Clasa a VI-a B", etc.
    const patterns = [
      /(?:clasa\s+a\s+)?([ivx]+|[0-9]+)(?:-a)?[\s\-_]*([a-z])?/i,
      /([0-9]+|[ivx]+)[\s\-_]*([a-z])?/i
    ];

    for (const pattern of patterns) {
      const match = cleanInput.match(pattern);
      if (match) {
        const num = match[1];
        const letter = match[2] || '';

        // Convert roman to arabic if needed
        const romanToArabic = {
          'i': '1', 'ii': '2', 'iii': '3', 'iv': '4', 'v': '5',
          'vi': '6', 'vii': '7', 'viii': '8', 'ix': '9', 'x': '10',
          'xi': '11', 'xii': '12'
        };

        if (romanToArabic[num.toLowerCase()]) {
          classNumber = romanToArabic[num.toLowerCase()];
        } else if (/^[0-9]+$/.test(num)) {
          classNumber = num;
        }

        if (letter) {
          classLetter = letter.toUpperCase();
        }
        break;
      }
    }

    if (classNumber) {
      const suffix = classLetter ? `-${classLetter}` : '';
      return `orar-${classNumber}${suffix}-${timestamp}.json`;
    }

    return `orar-${timestamp}.json`;
  };

  const handleExportJSON = async () => {
    try {
      const exportData = {
        config: {
          classDuration,
          breakDuration,
          startHour,
          startMinute,
          longBreakEnabled,
          longBreakAfter,
          longBreakDuration,
          numClasses,
          timetableName,
          transposedView
        },
        timetableData,
        legend,
        exportDate: new Date().toISOString()
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const exportFileDefaultName = getExportFileName();
      
      // Try modern File System Access API first (works in Chrome)
      if ('showSaveFilePicker' in window) {
        try {
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: exportFileDefaultName,
            types: [{
              description: 'JSON files',
              accept: { 'application/json': ['.json'] }
            }]
          });
          
          const writable = await fileHandle.createWritable();
          await writable.write(dataStr);
          await writable.close();
          
          alert('Orarul a fost exportat cu succes!');
          return;
        } catch (err) {
          // User cancelled or error occurred, fall back to old method
          if (err.name !== 'AbortError') {
            console.log('File System Access API failed, using fallback');
          }
        }
      }
      
      // Fallback for older browsers or if File System Access API failed
      // Use octet-stream to force download dialog in some browsers
      const blob = new Blob([dataStr], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      
      const linkElement = document.createElement('a');
      linkElement.href = url;
      linkElement.download = exportFileDefaultName;
      linkElement.style.display = 'none';
      
      // Force user interaction by requiring click
      document.body.appendChild(linkElement);
      linkElement.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(linkElement);
        URL.revokeObjectURL(url);
        alert('Orarul a fost exportat cu succes!');
      }, 100);
      
    } catch (error) {
      console.error('Export error:', error);
      alert('Eroare la export: ' + error.message);
    }
  };

  const handleImportJSON = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importData = JSON.parse(e.target.result);
        
        if (importData.config) {
          setClassDuration(importData.config.classDuration || 50);
          setBreakDuration(importData.config.breakDuration || 10);
          setStartHour(importData.config.startHour || 8);
          setStartMinute(importData.config.startMinute || 0);
          setLongBreakEnabled(importData.config.longBreakEnabled || false);
          setLongBreakAfter(importData.config.longBreakAfter || '');
          setLongBreakDuration(importData.config.longBreakDuration || 20);
          setNumClasses(importData.config.numClasses || 7);
          setTimetableName(importData.config.timetableName || '');
          setTransposedView(importData.config.transposedView !== false);
        }
        
        // Process legend first so we can check for custom values
        let importedLegend = legend;
        if (importData.legend) {
          // Handle both old object format and new array format
          if (Array.isArray(importData.legend)) {
            importedLegend = importData.legend;
            setLegend(importData.legend);
          } else {
            // Convert old object format to new array format
            const legendArray = Object.entries(importData.legend).map(([abbr, meaning]) => ({
              abbr, meaning
            }));
            importedLegend = legendArray;
            setLegend(legendArray);
          }
        }

        if (importData.timetableData) {
          setTimetableData(importData.timetableData);

          // Mark custom values (not in legend) as manual inputs
          const newManualInputs = {};
          Object.entries(importData.timetableData).forEach(([key, value]) => {
            if (value && !importedLegend.some(item => item.abbr === value)) {
              newManualInputs[key] = true;
            }
          });
          setManualInputs(newManualInputs);
        }

        alert('Orarul a fost importat cu succes!');
      } catch (error) {
        alert('Eroare la importul fișierului JSON. Verifică formatul.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleLegendChange = (abbreviation, meaning) => {
    setLegend(prev => {
      const existingIndex = prev.findIndex(item => item.abbr === abbreviation);
      if (existingIndex >= 0) {
        const newLegend = [...prev];
        newLegend[existingIndex] = { abbr: abbreviation, meaning };
        return newLegend;
      } else {
        return [...prev, { abbr: abbreviation, meaning }];
      }
    });
  };
  
  const startEditingLegend = (abbr, type) => {
    setEditingLegend({ abbr, type });
  };
  
  const handleLegendEdit = (oldAbbr, newValue, type) => {
    if (type === 'abbreviation') {
      if (newValue && newValue !== oldAbbr) {
        setLegend(prev => {
          const newLegend = [...prev];
          const index = newLegend.findIndex(item => item.abbr === oldAbbr);
          if (index >= 0) {
            newLegend[index] = { ...newLegend[index], abbr: newValue };
          }
          return newLegend;
        });
      }
    } else if (type === 'meaning') {
      if (newValue) {
        setLegend(prev => {
          const newLegend = [...prev];
          const index = newLegend.findIndex(item => item.abbr === oldAbbr);
          if (index >= 0) {
            newLegend[index] = { ...newLegend[index], meaning: newValue };
          }
          return newLegend;
        });
      }
    }
    setEditingLegend(null);
  };

  const removeLegendEntry = (abbreviation) => {
    setLegend(prev => prev.filter(item => item.abbr !== abbreviation));
  };

  const addLegendEntry = () => {
    const abbr = prompt('Introdu prescurtarea:');
    if (abbr && abbr.trim()) {
      const meaning = prompt('Introdu semnificația:');
      if (meaning && meaning.trim()) {
        handleLegendChange(abbr.trim(), meaning.trim());
      }
    }
  };

  // Legend drag and drop
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Cell drag and drop
  const handleCellDragStart = (e, day, classIndex) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ day, classIndex }));
    e.dataTransfer.effectAllowed = 'move';
    e.target.closest('td').classList.add('dragging');
  };

  const handleCellDragEnd = (e) => {
    e.target.closest('td')?.classList.remove('dragging');
  };

  const handleCellDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    e.target.closest('td')?.classList.add('drag-over');
  };

  const handleCellDragLeave = (e) => {
    e.target.closest('td')?.classList.remove('drag-over');
  };

  const handleCellDrop = (e, targetDay, targetClassIndex) => {
    e.preventDefault();
    e.target.closest('td')?.classList.remove('drag-over');

    try {
      const source = JSON.parse(e.dataTransfer.getData('application/json'));
      const sourceKey = `${source.day}-${source.classIndex}`;
      const targetKey = `${targetDay}-${targetClassIndex}`;

      if (sourceKey !== targetKey) {
        setTimetableData(prev => {
          const newData = { ...prev };
          const sourceValue = newData[sourceKey];
          const targetValue = newData[targetKey];

          // Swap values
          if (sourceValue) {
            newData[targetKey] = sourceValue;
          } else {
            delete newData[targetKey];
          }

          if (targetValue) {
            newData[sourceKey] = targetValue;
          } else {
            delete newData[sourceKey];
          }

          return newData;
        });

        // Swap manual input states too
        setManualInputs(prev => {
          const newInputs = { ...prev };
          const sourceManual = newInputs[sourceKey];
          const targetManual = newInputs[targetKey];

          if (sourceManual) {
            newInputs[targetKey] = true;
          } else {
            delete newInputs[targetKey];
          }

          if (targetManual) {
            newInputs[sourceKey] = true;
          } else {
            delete newInputs[sourceKey];
          }

          return newInputs;
        });
      }
    } catch (err) {
      // Ignore invalid drag data
    }
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const sourceIndex = parseInt(e.dataTransfer.getData('text/plain'));

    if (sourceIndex !== targetIndex) {
      setLegend(prev => {
        const newLegend = [...prev];
        const [draggedItem] = newLegend.splice(sourceIndex, 1);
        newLegend.splice(targetIndex, 0, draggedItem);
        return newLegend;
      });
    }
  };

  const sortLegendAlphabetically = () => {
    setLegend(prev => [...prev].sort((a, b) => a.abbr.localeCompare(b.abbr)));
  };

  return (
    <div className="app">
      <h1>
        <span className="screen-title">Generator Orar Școală</span>
        <span className="print-title">{formatClassName(timetableName) || 'Orar'}</span>
      </h1>
      
      <div className="controls">
        <h3>Configurare Orar</h3>
        
        <div className="control-group">
          <label>Nume orar:</label>
          <input
            type="text"
            value={timetableName}
            onChange={(e) => setTimetableName(e.target.value)}
            placeholder="ex: 6 B sau VI A"
            style={{width: '200px'}}
          />
        </div>
        
        <div className="control-group">
          <label>Durata oră (minute):</label>
          <input
            type="number"
            value={classDuration}
            onChange={(e) => setClassDuration(parseInt(e.target.value) || 50)}
            min="1"
            max="120"
          />
        </div>

        <div className="control-group">
          <label>Durata pauză (minute):</label>
          <input
            type="number"
            value={breakDuration}
            onChange={(e) => setBreakDuration(parseInt(e.target.value) || 10)}
            min="0"
            max="60"
          />
        </div>

        <div className="control-group">
          <label>Ora de început:</label>
          <select 
            value={startHour} 
            onChange={(e) => setStartHour(parseInt(e.target.value))}
          >
            {Array.from({length: 24}, (_, i) => (
              <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
            ))}
          </select>
          :
          <select 
            value={startMinute} 
            onChange={(e) => setStartMinute(parseInt(e.target.value))}
          >
            {[0, 15, 30, 45].map(minute => (
              <option key={minute} value={minute}>{minute.toString().padStart(2, '0')}</option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label>Numărul de ore pe zi:</label>
          <input
            type="number"
            value={numClasses}
            onChange={(e) => setNumClasses(parseInt(e.target.value) || 7)}
            min="1"
            max="15"
          />
        </div>

        <div className="control-group">
          <label>Pauză Mare:</label>
          <div className="toggle-switch">
            <input 
              type="radio" 
              id="break-no" 
              name="longBreak" 
              value="no" 
              checked={!longBreakEnabled} 
              onChange={() => setLongBreakEnabled(false)} 
            />
            <label htmlFor="break-no" className="toggle-option">NU</label>
            <input 
              type="radio" 
              id="break-yes" 
              name="longBreak" 
              value="yes" 
              checked={longBreakEnabled} 
              onChange={() => setLongBreakEnabled(true)} 
            />
            <label htmlFor="break-yes" className="toggle-option">DA</label>
          </div>
        </div>

        {longBreakEnabled && (
          <>
            <div className="control-group">
              <label>Pauză mare după ora:</label>
              <select
                value={longBreakAfter}
                onChange={(e) => setLongBreakAfter(e.target.value)}
              >
                <option value="">Selectează...</option>
                {getAvailableBreakTimes().map((slot) => (
                  <option key={slot.endTime} value={slot.endTime}>
                    {slot.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="control-group">
              <label>Durata pauză mare:</label>
              <input
                type="number"
                value={longBreakDuration}
                onChange={(e) => setLongBreakDuration(parseInt(e.target.value) || 20)}
                style={{marginRight: '5px'}}
                min="1"
                max="120"
              />
              <span>(min)</span>
            </div>
          </>
        )}
        
        <div className="control-group">
          <label>Afișare:</label>
          <div className="toggle-switch">
            <input
              type="radio"
              id="view-transposed"
              name="viewMode"
              value="transposed"
              checked={transposedView}
              onChange={() => setTransposedView(true)}
            />
            <label htmlFor="view-transposed" className="toggle-option">Ore pe rânduri</label>
            <input
              type="radio"
              id="view-normal"
              name="viewMode"
              value="normal"
              checked={!transposedView}
              onChange={() => setTransposedView(false)}
            />
            <label htmlFor="view-normal" className="toggle-option">Zile pe rânduri</label>
          </div>
        </div>

        <div className="control-group">
          <button onClick={handlePrint} className="print-button">
            📄 Printează Orarul
          </button>
        </div>
        
        <div className="control-group">
          <button onClick={handleExportJSON} className="export-button">
            💾 Exportă
          </button>
          <input
            type="file"
            accept=".json"
            onChange={handleImportJSON}
            style={{display: 'none'}}
            id="import-file"
          />
          <button onClick={() => document.getElementById('import-file').click()} className="import-button">
            📁 Importă
          </button>
        </div>
      </div>

      <table className="timetable">
        <thead>
          <tr>
            <th className="empty-header"></th>
            {transposedView ? (
              DAYS.map((day) => (
                <th key={day} className="day-header-top">
                  {day}
                </th>
              ))
            ) : (
              timeSlots.map((slot) => (
                <th key={slot.index} className="time-header">
                  {slot.timeRange}
                </th>
              ))
            )}
          </tr>
        </thead>
        <tbody>
          {transposedView ? (
            timeSlots.map((slot) => (
              <tr key={slot.index}>
                <td className="time-header-side">{slot.timeRange}</td>
                {DAYS.map((day) => (
                  <td
                    key={day}
                    draggable={!manualInputs[`${day}-${slot.index}`]}
                    onDragStart={(e) => handleCellDragStart(e, day, slot.index)}
                    onDragEnd={handleCellDragEnd}
                    onDragOver={handleCellDragOver}
                    onDragLeave={handleCellDragLeave}
                    onDrop={(e) => handleCellDrop(e, day, slot.index)}
                  >
                    <span className="print-only">{getDisplayText(day, slot.index)}</span>
                    <div className="screen-only">
                      {manualInputs[`${day}-${slot.index}`] ? (
                        <input
                          type="text"
                          value={getSubjectValue(day, slot.index)}
                          onChange={(e) => handleManualInputChange(day, slot.index, e.target.value)}
                          onBlur={() => handleManualInputBlur(day, slot.index)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleManualInputBlur(day, slot.index);
                            }
                            if (e.key === 'Escape') {
                              setManualInputs(prev => {
                                const newInputs = { ...prev };
                                delete newInputs[`${day}-${slot.index}`];
                                return newInputs;
                              });
                            }
                          }}
                          placeholder="Introdu manual..."
                          autoFocus
                          className="manual-input"
                        />
                      ) : (
                        <select
                          value={getSubjectValue(day, slot.index)}
                          onChange={(e) => handleSubjectChange(day, slot.index, e.target.value)}
                          className="subject-dropdown"
                        >
                          <option value="">Selectează...</option>
                          {legend.map((item) => (
                            <option key={item.abbr} value={item.abbr}>{item.abbr} - {item.meaning}</option>
                          ))}
                          <option value="MANUAL">✏️ Manual...</option>
                          <option value="GOL">🗑️ Gol</option>
                        </select>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            ))
          ) : (
            DAYS.map((day) => (
              <tr key={day}>
                <td className="day-header">{day}</td>
                {timeSlots.map((slot) => (
                  <td
                    key={slot.index}
                    draggable={!manualInputs[`${day}-${slot.index}`]}
                    onDragStart={(e) => handleCellDragStart(e, day, slot.index)}
                    onDragEnd={handleCellDragEnd}
                    onDragOver={handleCellDragOver}
                    onDragLeave={handleCellDragLeave}
                    onDrop={(e) => handleCellDrop(e, day, slot.index)}
                  >
                    <span className="print-only">{getDisplayText(day, slot.index)}</span>
                    <div className="screen-only">
                      {manualInputs[`${day}-${slot.index}`] ? (
                        <input
                          type="text"
                          value={getSubjectValue(day, slot.index)}
                          onChange={(e) => handleManualInputChange(day, slot.index, e.target.value)}
                          onBlur={() => handleManualInputBlur(day, slot.index)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleManualInputBlur(day, slot.index);
                            }
                            if (e.key === 'Escape') {
                              setManualInputs(prev => {
                                const newInputs = { ...prev };
                                delete newInputs[`${day}-${slot.index}`];
                                return newInputs;
                              });
                            }
                          }}
                          placeholder="Introdu manual..."
                          autoFocus
                          className="manual-input"
                        />
                      ) : (
                        <select
                          value={getSubjectValue(day, slot.index)}
                          onChange={(e) => handleSubjectChange(day, slot.index, e.target.value)}
                          className="subject-dropdown"
                        >
                          <option value="">Selectează...</option>
                          {legend.map((item) => (
                            <option key={item.abbr} value={item.abbr}>{item.abbr} - {item.meaning}</option>
                          ))}
                          <option value="MANUAL">✏️ Manual...</option>
                          <option value="GOL">🗑️ Gol</option>
                        </select>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      
      <div className="legend-section">
        <h3>Legenda Prescurtări</h3>
        <div className="legend-controls">
          <button onClick={addLegendEntry} className="add-legend-button">
            ➕ Adaugă Prescurtare
          </button>
          <button onClick={sortLegendAlphabetically} className="sort-legend-button">
            🔤 Sortează Alfabetic
          </button>
        </div>
        <div className="legend-list">
          {legend.map((item, index) => (
            <div
              key={item.abbr}
              className="legend-item"
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
            >
              <span className="drag-handle" title="Drag pentru reordonare">⋮⋮</span>
              {editingLegend && editingLegend.abbr === item.abbr && editingLegend.type === 'abbreviation' ? (
                <input
                  type="text"
                  defaultValue={item.abbr}
                  onBlur={(e) => handleLegendEdit(item.abbr, e.target.value, 'abbreviation')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleLegendEdit(item.abbr, e.target.value, 'abbreviation');
                    }
                    if (e.key === 'Escape') {
                      setEditingLegend(null);
                    }
                  }}
                  autoFocus
                  className="legend-edit-input"
                />
              ) : (
                <span
                  className="legend-abbr editable"
                  onClick={() => startEditingLegend(item.abbr, 'abbreviation')}
                  title="Click pentru editare"
                >
                  {item.abbr}
                </span>
              )}
              <span className="legend-separator"> = </span>
              {editingLegend && editingLegend.abbr === item.abbr && editingLegend.type === 'meaning' ? (
                <input
                  type="text"
                  defaultValue={item.meaning}
                  onBlur={(e) => handleLegendEdit(item.abbr, e.target.value, 'meaning')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleLegendEdit(item.abbr, e.target.value, 'meaning');
                    }
                    if (e.key === 'Escape') {
                      setEditingLegend(null);
                    }
                  }}
                  autoFocus
                  className="legend-edit-input"
                />
              ) : (
                <span
                  className="legend-meaning editable"
                  onClick={() => startEditingLegend(item.abbr, 'meaning')}
                  title="Click pentru editare"
                >
                  {item.meaning}
                </span>
              )}
              <button
                onClick={() => removeLegendEntry(item.abbr)}
                className="remove-legend-button"
                title="Șterge prescurtarea"
              >
                ❌
              </button>
            </div>
          ))}
          {legend.length === 0 && (
            <p className="no-legend">Nu sunt prescurtări definite.</p>
          )}
        </div>
      </div>

      <footer className="app-footer">
        <p>creat de <a href="https://ironops.net" target="_blank" rel="noopener noreferrer">ironops.net</a> și claudel</p>
      </footer>
    </div>
  );
}

export default App;